/**
 * AAR replay map controller (#180) — drives the Cesium side of the incident
 * time replay: historical anomaly dots recoloured per Law 3 at the scrubbed
 * time, a highlight ring on the current audit event's target, and the shared
 * capture/restore idiom (lib/cameraFlight.ts, #252) so exiting lands the
 * operator exactly on their prior view.
 *
 * Law 6 (motion): every replay entity is STATIC. The urgent-red pulse is
 * reserved for LIVE unacked anomalies; a replayed urgent is history and renders
 * as a static red dot. Entities change only when the operator scrubs
 * (setTime), so a 5s frame-diff of an idle replay shows zero animated pixels.
 * The #238 aircraft/vessel track markers obey the same rule: their position is
 * re-sampled per scrub, never per frame — an idle replay's tracks are frozen.
 *
 * Time machinery: replay time is a plain epoch-ms scalar fed by the overlay's
 * slider — NOT viewer.clock / CZML. The shared clock keeps driving the live
 * aircraft SampledPositionProperty motion (#173) underneath; rebinding it to a
 * historical window would freeze or teleport live traffic. Lifecycle recolour
 * is a step function, so sampled interpolation buys nothing.
 *
 * One deliberate Law-3 deviation, AAR-scoped: a cleared anomaly's grey ghost
 * does not auto-leave after 60s — while scrubbed past its end it stays visible
 * as a ghost, because "where it was and when it cleared" is exactly what an
 * after-action review is for.
 */
import * as Cesium from 'cesium';
import type { AarReplayResponse } from '@tsr/shared-types';
import {
  captureCameraPose,
  flyToSettled,
  type CameraPose,
  type SettleCamera,
} from './lib/cameraFlight';
import {
  buildReplayTimeline,
  buildTrackTimelines,
  anomalyStateAt,
  latestEventAt,
  clampReplayTime,
  trackVisibleAt,
  type AarReplayTimeline,
  type AarTrackTimeline,
} from './aarReplayTimeline';
import { ANOMALY_MAP_RED } from './styles/responseLevel';
import {
  ACK_STEEL,
  ACK_STEEL_ALPHA,
  CLEARED_GHOST,
  CLEARED_GHOST_ALPHA,
} from './layers/anomalyMarker';

export type AarReplayEndReason = 'stopped' | 'destroyed';

/** Imperative handle MapView exposes to App (flythrough/floodInundation idiom). */
export interface AarReplayController {
  /** Enter replay: capture the prior view, fly to the incident, mount the
   *  historical entities at the window start. No-op if already active. */
  start(data: AarReplayResponse, onEnded?: (reason: AarReplayEndReason) => void): void;
  /** Scrub to an epoch-ms position (clamped to the replay window). */
  setTime(tMs: number): void;
  /** Exit replay: remove entities and fly back to the captured prior view. */
  stop(): void;
  isActive(): boolean;
}

export interface AarReplayHandle extends AarReplayController {
  /** Synchronous teardown for viewer unmount — no restore flight. */
  destroy(): void;
}

/** Replay overview camera height — regional framing of a 50 km radius story. */
const REPLAY_VIEW_HEIGHT_M = 160_000;
const REPLAY_FLY_DURATION_S = 2.0;
const RESTORE_FLY_DURATION_S = 2.0;

const DOT_PX = 9;
const INCIDENT_PX = 7;
const HIGHLIGHT_PX = 22;
const HIGHLIGHT_AMBER = '#f59e0b';

// Track motion (#238). Marker + static path per segment; non-pulsing (Law 6).
const TRACK_MARKER_PX = 7;
const TRACK_PATH_WIDTH = 1.5;
const TRACK_PATH_ALPHA = 0.35;
const TRACK_COLOR: Record<'aircraft' | 'vessel', string> = {
  aircraft: '#38bdf8', // sky — distinct from every Law-3 lifecycle colour
  vessel: '#2dd4bf', // teal
};

/** The viewer slice the controller drives — structural so unit tests run on a
 *  fake (no WebGL). The real Cesium.Viewer satisfies it. */
export interface AarReplayViewer {
  camera: SettleCamera;
  entities: {
    add(entity: unknown): unknown;
    remove(entity: unknown): boolean;
  };
  scene: { requestRender?: () => void };
  isDestroyed?(): boolean;
}

/** Minimal mutable surface of the entities we add (structural for tests). */
interface ReplayEntity {
  show: boolean;
  position?: unknown;
  point?: { color: unknown };
}

const rgba = (hex: string, alpha: number): Cesium.Color =>
  Cesium.Color.fromCssColorString(hex).withAlpha(alpha);

export function installAarReplay(viewer: AarReplayViewer): AarReplayHandle {
  let active = false;
  let priorPose: CameraPose | null = null;
  let onEnded: ((reason: AarReplayEndReason) => void) | null = null;
  let timeline: AarReplayTimeline | null = null;
  /** Entities this controller owns, removed on stop/destroy. */
  let owned: unknown[] = [];
  let trackEntities: ReplayEntity[] = [];
  /** One moving marker per aircraft/vessel track segment (#238), positioned by
   *  sampling its SampledPositionProperty at the scrubbed time. */
  let motionTracks: Array<{
    timeline: AarTrackTimeline;
    sampled: Cesium.SampledPositionProperty;
    entity: ReplayEntity;
  }> = [];
  let highlight: ReplayEntity | null = null;
  /** anomaly id → position, so an event targeting an anomaly highlights it. */
  let targetPositions = new Map<string, Cesium.Cartesian3>();
  let incidentPosition: Cesium.Cartesian3 | null = null;

  const removeAll = (): void => {
    for (const e of owned) {
      if (!viewer.isDestroyed?.()) viewer.entities.remove(e);
    }
    owned = [];
    trackEntities = [];
    motionTracks = [];
    highlight = null;
    targetPositions = new Map();
    incidentPosition = null;
    timeline = null;
  };

  const emitEnded = (reason: AarReplayEndReason): void => {
    const cb = onEnded;
    onEnded = null;
    active = false;
    cb?.(reason);
  };

  const styleFor = (state: 'urgent' | 'acknowledged' | 'cleared'): Cesium.Color => {
    switch (state) {
      case 'urgent':
        return rgba(ANOMALY_MAP_RED, 0.95);
      case 'acknowledged':
        return rgba(ACK_STEEL, ACK_STEEL_ALPHA);
      case 'cleared':
        return rgba(CLEARED_GHOST, CLEARED_GHOST_ALPHA);
    }
  };

  return {
    start(data, ended): void {
      if (active || viewer.isDestroyed?.()) return;
      active = true;
      onEnded = ended ?? null;
      priorPose = captureCameraPose(viewer.camera);
      timeline = buildReplayTimeline(data);

      incidentPosition = Cesium.Cartesian3.fromDegrees(data.incident.lon, data.incident.lat);

      // Incident anchor — static slate diamond-stand-in (point + label).
      owned.push(
        viewer.entities.add({
          position: incidentPosition,
          point: {
            pixelSize: INCIDENT_PX,
            color: rgba('#94a3b8', 0.9),
            outlineColor: rgba('#0f172a', 0.9),
            outlineWidth: 1,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
          },
          label: {
            text: data.incident.incident_number,
            font: '11px sans-serif',
            fillColor: rgba('#cbd5e1', 0.95),
            pixelOffset: new Cesium.Cartesian2(0, -16),
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
          },
        }),
      );

      // One static dot per anomaly track; colour/show driven by setTime only.
      for (const track of timeline.tracks) {
        const position = Cesium.Cartesian3.fromDegrees(track.anomaly.lon, track.anomaly.lat);
        targetPositions.set(track.anomaly.id, position);
        const entity = viewer.entities.add({
          position,
          show: false,
          point: {
            pixelSize: DOT_PX,
            color: styleFor('urgent'),
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
          },
        }) as unknown as ReplayEntity;
        owned.push(entity);
        trackEntities.push(entity);
      }

      // Aircraft/vessel motion tracks (#238): per segment, a static dim path
      // polyline + ONE marker whose position is the segment's
      // SampledPositionProperty sampled at the scrubbed time — the #173 live-
      // motion idiom (linear interpolation, HOLD at the ends), driven by the
      // overlay's epoch-ms scalar instead of viewer.clock. Law 6 holds: the
      // marker only moves when the operator scrubs; nothing pulses.
      for (const tl of buildTrackTimelines(data.tracks)) {
        const colour = TRACK_COLOR[tl.segment.entity_kind];
        const pathPositions = tl.fixes.map((f) =>
          Cesium.Cartesian3.fromDegrees(f.lon, f.lat, f.altM ?? 0),
        );
        if (pathPositions.length >= 2) {
          owned.push(
            viewer.entities.add({
              polyline: {
                positions: pathPositions,
                width: TRACK_PATH_WIDTH,
                material: rgba(colour, TRACK_PATH_ALPHA),
              },
            }),
          );
        }
        const sampled = new Cesium.SampledPositionProperty();
        sampled.setInterpolationOptions({
          interpolationDegree: 1,
          interpolationAlgorithm: Cesium.LinearApproximation,
        });
        sampled.forwardExtrapolationType = Cesium.ExtrapolationType.HOLD;
        sampled.backwardExtrapolationType = Cesium.ExtrapolationType.HOLD;
        tl.fixes.forEach((f, i) => {
          sampled.addSample(
            Cesium.JulianDate.fromDate(new Date(f.tMs)),
            pathPositions[i] as Cesium.Cartesian3,
          );
        });
        const entity = viewer.entities.add({
          position: pathPositions[0],
          show: false,
          point: {
            pixelSize: TRACK_MARKER_PX,
            color: rgba(colour, 0.95),
            outlineColor: rgba('#0f172a', 0.9),
            outlineWidth: 1,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
          },
          label: {
            text: tl.segment.label,
            font: '10px sans-serif',
            fillColor: rgba(colour, 0.95),
            pixelOffset: new Cesium.Cartesian2(0, -14),
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
          },
        }) as unknown as ReplayEntity;
        owned.push(entity);
        motionTracks.push({ timeline: tl, sampled, entity });
      }

      // Highlight ring for the current audit event's target (hollow amber).
      highlight = viewer.entities.add({
        position: incidentPosition,
        show: false,
        point: {
          pixelSize: HIGHLIGHT_PX,
          color: Cesium.Color.TRANSPARENT,
          outlineColor: rgba(HIGHLIGHT_AMBER, 0.9),
          outlineWidth: 2,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
      }) as unknown as ReplayEntity;
      owned.push(highlight);

      this.setTime(timeline.startMs);

      void flyToSettled(
        viewer,
        {
          destination: Cesium.Cartesian3.fromDegrees(
            data.incident.lon,
            data.incident.lat,
            REPLAY_VIEW_HEIGHT_M,
          ),
        },
        REPLAY_FLY_DURATION_S,
      );
    },

    setTime(tMs): void {
      if (!active || !timeline) return;
      const t = clampReplayTime(timeline, tMs);

      timeline.tracks.forEach((track, i) => {
        const entity = trackEntities[i];
        if (!entity) return;
        const state = anomalyStateAt(track, t);
        if (state === 'pre') {
          entity.show = false;
          return;
        }
        entity.show = true;
        if (entity.point) entity.point.color = styleFor(state);
      });

      // Track markers (#238): show inside [first fix, last fix + hold] and sit
      // at the interpolated position for the scrubbed moment. Hidden before
      // the first fix — no back-projection of unknown positions.
      const julian = Cesium.JulianDate.fromDate(new Date(t));
      for (const mt of motionTracks) {
        const visible = trackVisibleAt(mt.timeline, t);
        mt.entity.show = visible;
        if (visible) {
          const pos = mt.sampled.getValue(julian);
          if (pos) mt.entity.position = pos;
        }
      }

      // Highlight the most recent event's target: an anomaly when the payload
      // target resolves to one we placed, else the incident anchor.
      const latest = latestEventAt(timeline.events, t);
      if (highlight) {
        if (!latest) {
          highlight.show = false;
        } else {
          highlight.show = true;
          highlight.position =
            (latest.event.target ? targetPositions.get(latest.event.target) : undefined) ??
            incidentPosition ??
            highlight.position;
        }
      }

      viewer.scene.requestRender?.();
    },

    stop(): void {
      if (!active) return;
      const pose = priorPose;
      priorPose = null;
      removeAll();
      if (pose && !viewer.isDestroyed?.()) {
        // Settle grammar (#252): a starved restore flight lands by setView, so
        // the exit pose is the captured pose exactly.
        void flyToSettled(
          viewer,
          {
            destination: pose.destination,
            orientation: { heading: pose.heading, pitch: pose.pitch, roll: pose.roll },
          },
          RESTORE_FLY_DURATION_S,
        ).then(() => emitEnded('stopped'));
      } else {
        emitEnded('stopped');
      }
    },

    isActive(): boolean {
      return active;
    },

    destroy(): void {
      priorPose = null;
      removeAll();
      if (active) emitEnded('destroyed');
    },
  };
}
